import React, { useState, useEffect } from "react";
import { Box, Paper, Grid } from "@mui/material";
import YFSLabelContainer from "../ProductYFSLabel/YFSLabelContainer";
import YFSLabelVerifications from "../ProductYFSLabel/YFSLabelVerifications";
import productInfoIcon from "../../assets/yfs_label/product_yfs_label/icons/yfslabel_product_icon.svg";
import productScopeIcon from "../../assets/yfs_label/product_yfs_label/icons/yfslabel_scopes_icon.svg";
import productVariationIcon from "../../assets/yfs_label/product_yfs_label/icons/yfslabel_variations_icon.svg";
import YFSLabelItem from "../ProductYFSLabel/YFSLabelItem";
import CustomStatusCheckbox from "../ProductYFSLabel/CustomStatusCheckbox";
import CustomStatusCheckboxView from "../ProductYFSLabel/CustomStatusCheckboxView";
import "../ProductYFSLabel/index.css";
import { useTranslation } from "react-i18next";

const initializePublicItems = (data) => {
  if (!data || typeof data !== "object") {
    return {};
  }

  const initialState = {};
  Object.keys(data).forEach((key) => {
    initialState[key] = null;
  });
  return initialState;
};

const CompanySummary = ({
  companyData,
  editMode,
  setCompletePublicItems,
  categories,
  setCategories,
  yfsRetrieve,
  publicizedItems,
}) => {
  const [publicItems, setPublicItems] = useState(
    publicizedItems ? publicizedItems : initializePublicItems(companyData)
  );
  const { t } = useTranslation();

  useEffect(() => {
    if (setCompletePublicItems) {
      setCompletePublicItems((prevItems) => ({
        ...prevItems,
        ...publicItems,
      }));
    }
  }, [publicItems, categories, setCompletePublicItems]);

  const handlePublicToggle = (key, value) => {
    setPublicItems((prev) => {
      const newState = { ...prev };
      newState[key] = newState[key] === value ? null : value;
      return newState;
    });
  };

  const handleCategoryChange = (key, category) => {
    setCategories((prev) => {
      const newCategories = { ...prev };
      newCategories[key] = category;
      return newCategories;
    });
  };

  const renderEditControls = (key, value) => {
    let checked = publicItems[key] === value;
    let categoryKey = key;

    if (!editMode && yfsRetrieve)
      return (
        <CustomStatusCheckboxView
          itemKey={categoryKey}
          categories={categories}
          checked={checked}
        />
      );
    else if (!editMode) {
      return;
    }

    return (
      <CustomStatusCheckbox
        itemKey={categoryKey}
        checked={checked}
        onChange={() => handlePublicToggle(key, value)}
        categories={categories}
        handleCategoryChange={(itemKey, category) =>
          handleCategoryChange(key, category)
        }
      />
    );
  };

  const renderInfoGroup = () => {
    // Collect all items into a single array
    const allItems = [
      {
        type: "basic",
        name: t("businessName"),
        value: companyData.business_name || "N/A",
        controlKey: "business_name",
        controlValue: companyData.business_name,
      },
      {
        type: "basic",
        name: t("email"),
        value: companyData.business_email || "N/A",
        controlKey: "business_email",
        controlValue: companyData.business_email,
      },
      {
        type: "basic",
        name: t("website"),
        value: companyData.website || "N/A",
        controlKey: "website",
        controlValue: companyData.website,
      },
      {
        type: "basic",
        name: t("address"),
        value: companyData.address || "N/A",
        controlKey: "address",
        controlValue: companyData.address,
      },
      {
        type: "basic",
        name: t("totalFacilities"),
        value: companyData.total_facilities || "N/A",
        controlKey: "total_facilities",
        controlValue: companyData.total_facilities,
      },
      {
        type: "basic",
        name: t("totalEmployees"),
        value: companyData.total_employees || "N/A",
        controlKey: "total_employees",
        controlValue: companyData.total_employees,
      },
    ];

    // Calculate the midpoint to split items evenly
    const midPoint = Math.ceil(allItems.length / 2);

    // Split items into two columns
    const firstColumnItems = allItems.slice(0, midPoint);
    const secondColumnItems = allItems.slice(midPoint);

    const renderItem = (item) => {
      return (
        <Grid item xs={12} key={item.name} display="flex">
          <YFSLabelItem name={item.name} value={item.value} />
          {renderEditControls(item.controlKey, item.controlValue)}
        </Grid>
      );
    };

    return (
      <Paper
        elevation={0}
        sx={{ p: 2, mb: 2 }}
        className="product-info-container"
      >
        <img
          src={productVariationIcon}
          alt="Company Info Icon"
          width={30}
          height={30}
          style={{ position: "absolute", top: "-10px", left: "3px" }}
        />
        <Grid container spacing={2}>
          {/* First Column */}
          <Grid item xs={12} md={6}>
            <Grid container spacing={2}>
              {firstColumnItems.map((item) => renderItem(item))}
            </Grid>
          </Grid>

          {/* Second Column */}
          <Grid item xs={12} md={6}>
            <Grid container spacing={2}>
              {secondColumnItems.map((item) => renderItem(item))}
            </Grid>
          </Grid>
        </Grid>
      </Paper>
    );
  };

  const renderEnvironmentalImpactGroup = () => (
    <Paper elevation={0} sx={{ p: 2, mb: 2 }} className="product-env-container">
      <img
        src={productInfoIcon}
        alt="Environmental Impact Icon"
        width={30}
        height={30}
        style={{ position: "absolute", top: "-10px", left: "-2px" }}
      />
      <Grid container spacing={2}>
        {[
          { key: "total_virgin", label: t("virginMaterials") },
          { key: "total_recycle", label: t("recycledMaterials") },
          { key: "water_estimate", label: t("waterUsed") },
          { key: "total_surplus_material", label: t("surplusMaterials") },
          { key: "total_packaging", label: t("packaging") },
          { key: "transport_distance", label: t("transportDistance") },
          { key: "total_energy_used", label: t("energyUsed") },
          { key: "max_energy_used", label: t("maxEnergyUsed") },
        ].map(({ key, label }) => (
          <Grid item xs={12} key={key}>
            <Box
              display="flex"
              alignItems="center"
              width="100%"
              sx={{
                "& > *:first-of-type": {
                  flexGrow: 1,
                },
              }}
            >
              <YFSLabelItem
                name={label}
                value={companyData[key] || "N/A"}
                unit={
                  key !== "max_energy_used"
                    ? key.includes("distance")
                      ? "km"
                      : key.includes("total_energy_used")
                      ? "mj"
                      : key.includes("water")
                      ? "L"
                      : "kg"
                    : ""
                }
              />
              {renderEditControls(key, companyData[key])}
            </Box>
          </Grid>
        ))}
      </Grid>
    </Paper>
  );

  const renderCompanyScopes = () => (
    <Paper
      elevation={0}
      sx={{ p: 2, mb: 2 }}
      className="product-scope-container"
    >
      <img
        src={productScopeIcon}
        alt="Carbon Scope Icon"
        width={30}
        height={30}
        style={{ position: "absolute", top: "-10px", left: "-2px" }}
      />
      <Grid container spacing={2}>
        {[
          { key: "scope_1", label: t("scope1") },
          { key: "scope_2", label: t("scope2") },
          { key: "scope_3", label: t("scope3") },
          { key: "total_business_co2", label: t("totalCO2") },
          { key: "total_outside_scope", label: t("outsideScope") },
        ].map(({ key, label }) => (
          <Grid item xs={12} key={key}>
            <Box
              display="flex"
              alignItems="center"
              width="100%"
              sx={{
                "& > *:first-of-type": {
                  flexGrow: 1,
                },
              }}
            >
              <YFSLabelItem
                name={label}
                value={
                  companyData[key] !== undefined
                    ? typeof companyData[key] === "number"
                      ? companyData[key].toFixed(2)
                      : companyData[key]
                    : "N/A"
                }
                unit="(CO₂e)"
              />
              {renderEditControls(key, companyData[key])}
            </Box>
          </Grid>
        ))}
      </Grid>
    </Paper>
  );

  return (
    <YFSLabelContainer>
      <YFSLabelVerifications editMode={editMode} />
      {renderInfoGroup()}
      <div style={{ display: "flex", marginBottom: "80px" }}>
        <div style={{ width: "50%" }}>{renderCompanyScopes()}</div>
        <div style={{ marginLeft: "22px", width: "50%" }}>
          {renderEnvironmentalImpactGroup()}
        </div>
      </div>
    </YFSLabelContainer>
  );
};

export default CompanySummary;
